import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DateInput } from "../../components/ui/DateInput";
import { LockedFeatureEmptyState } from "../../components/billing/LockedFeatureEmptyState";
import { NotEnoughDataEmptyState } from "../../components/dashboard/NotEnoughDataEmptyState";
import SectionCard from "../../components/dashboard/SectionCard";
import PlansModal from "../../components/billing/PlansModal";
import { PatientsReport, RevenueReport, DiagnosesReport, PnlReport, CashFlowReport, BreakdownReport } from "../reports/ReportsPage";
import {
  ReportsAPI, PatientTrendBucket, RevenueBucket, DiagnosisRow,
  PnlBucket, CashFlowBucket, BreakdownRow, PnlPeriod, ConsolidatedPnlRow,
} from "../../services/reports.service";
import ClinicGroupAPI, { ClinicGroup } from "../../services/clinicGroup.service";
import { ClinicGroupPanel } from "../../components/accounting/ClinicGroupPanel";
import Sidebar from "../../components/Sidebar";
import { logger } from "../../utils/logger";

import { AppHeader } from "../../components/layout/AppHeader";
import DownloadReportButton from "../../components/DownloadReportButton";
import { useAddPatient } from "../../contexts/AddPatientContext";
import { useBookAppointment } from "../../contexts/BookAppointmentContext";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useRole } from "../../hooks/useRole";
import { TrialBanner, TrialExpiredModal, TrialStatusDetail } from "../../components/billing";
import { ClinicClosureBanner } from "../../components/billing/ClinicClosureBanner";

import {
  DashboardAPI,
  DashboardMetrics,
  DoctorDashboardSummary,
  ReceptionDashboardSummary,
} from "../../services/dashboard.service";
import { SettingsAPI } from "../../services/settings.service";
import { formatINR } from "../../components/dashboard/formatINR";

import KpiCardComponent from "../../components/dashboard/KpiCard";
import QuickActions from "../../components/dashboard/QuickActions";
import TodaysAppointments from "../../components/dashboard/TodaysAppointments";
import ActiveDoctorsPanel from "../../components/dashboard/ActiveDoctorsPanel";
import DepartmentOverview from "../../components/dashboard/DepartmentOverview";
import RevenueTrendCard from "../../components/dashboard/RevenueTrendCard";
import PrescriptionsCard from "../../components/dashboard/PrescriptionsCard";
import SystemAlerts from "../../components/dashboard/SystemAlerts";
import PatientActivityCard from "../../components/dashboard/PatientActivityCard";
import DashboardTabs, { DashboardTab } from "../../components/dashboard/DashboardTabs";

// A bare UUID (e.g. a Cognito sub) must never be shown as a person's name/email.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const humanLabel = (s: string): string => {
  const t = (s ?? "").trim();
  return t && !UUID_RE.test(t) ? t : "";
};

const DAILY_TIPS = [
  "Document SOAP notes immediately after each visit — it improves accuracy and saves time at end of day.",
  "Use the AI Scribe to transcribe consultations in real time and auto-fill your SOAP note.",
  "Keep your availability roster updated weekly so patients and reception can book accurately.",
  "Review pending visits each morning to prioritise follow-up patients.",
  "Add allergy information early in a patient's profile — it's critical for safe prescribing.",
  "Use the prescription search to avoid drug interactions before finalising a prescription.",
  "Short consultation notes are better than none — even a single sentence helps future review.",
];

const DailyTipCard: React.FC = () => {
  const tip = DAILY_TIPS[new Date().getDay() % DAILY_TIPS.length];
  return (
    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-100 shadow-sm p-5 flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
        </div>
        <p className="text-sm font-semibold text-slate-900">Daily Tip</p>
      </div>
      <p className="text-xs text-slate-600 leading-relaxed flex-1">{tip}</p>
    </div>
  );
};

function _isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
function _todayIso(): string { return new Date().toISOString().slice(0, 10); }

// Minimum data points (within the selected range) before a chart renders —
// a near-empty chart reads as broken, not as "not much has happened yet".
// Mirrors ReportsPage.tsx's gating for the same metrics.
const REPORT_MIN_DATA_POINTS = 5;

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { openAddPatientModal } = useAddPatient();
  const { openBookAppointmentModal } = useBookAppointment();

  const [userName, setUserName] = useState("");
  const [pendingVisitCount, setPendingVisitCount] = useState<number | null>(null);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [doctorSummary, setDoctorSummary] = useState<DoctorDashboardSummary | null>(null);
  const [receptionSummary, setReceptionSummary] = useState<ReceptionDashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DashboardTab>("Overview");
  const [showTrialDetail, setShowTrialDetail] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);

  // Report tabs state
  const [reportPreset, setReportPreset] = useState<"7" | "30" | "90" | "custom">("30");
  const [reportFrom, setReportFrom] = useState<string>(_isoDaysAgo(30));
  const [reportTo, setReportTo] = useState<string>(_todayIso());
  const [reportPatients, setReportPatients] = useState<PatientTrendBucket[] | null>(null);
  const [reportRevenue, setReportRevenue] = useState<RevenueBucket[] | null>(null);
  const [reportDiagnoses, setReportDiagnoses] = useState<DiagnosisRow[] | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportExporting, setReportExporting] = useState(false);

  const reportPatientsCount = (reportPatients ?? []).reduce((sum, b) => sum + b.new_patients + b.returning_patients, 0);
  const reportRevenueCount = (reportRevenue ?? []).reduce((sum, b) => sum + b.invoice_count, 0);
  const reportDiagnosesCount = (reportDiagnoses ?? []).reduce((sum, r) => sum + r.count, 0);

  // Accounting tab state — Admin-only, NOT feature-flag gated (confirmed by
  // product, 2026-07-23): loads independently of isGated below.
  const [accountingSubTab, setAccountingSubTab] = useState<"pnl" | "cash-flow" | "breakdown">("pnl");
  const [pnlPeriod, setPnlPeriod] = useState<PnlPeriod>("month");
  const [pnl, setPnl] = useState<PnlBucket[] | null>(null);
  const [cashFlow, setCashFlow] = useState<CashFlowBucket[] | null>(null);
  const [breakdown, setBreakdown] = useState<BreakdownRow[] | null>(null);
  const [myGroup, setMyGroup] = useState<ClinicGroup | null>(null);
  const [consolidated, setConsolidated] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<string>("all");
  const [consolidatedRows, setConsolidatedRows] = useState<ConsolidatedPnlRow[] | null>(null);
  const [accountingLoading, setAccountingLoading] = useState(false);
  const [accountingError, setAccountingError] = useState<string | null>(null);
  const [accountingExporting, setAccountingExporting] = useState(false);
  // Bumped to re-run the admin metrics effect — e.g. after a Today's
  // Appointments three-dot action so the KPIs/list reflect the change.
  const [metricsKey, setMetricsKey] = useState(0);
  const refreshMetrics = () => setMetricsKey((k) => k + 1);

  useEffect(() => {
    window.addEventListener("doctorUpdated", refreshMetrics);
    return () => window.removeEventListener("doctorUpdated", refreshMetrics);
  }, []);

  const {
    isAdmin: isAdminRole,
    isDoctor,
    isReception,
    can_prescribe,
    isLoaded: roleLoaded,
    canOr,
    canAnyOr,
  } = useRole();

  // Drop a shortcut whose grant is gone rather than rendering it dead — the modal
  // behind it would only 403 on submit. Legacy fallback `true`: every clinic role
  // had these before RBAC, so a backend that sends no grants keeps them.
  // Matched on onClick identity (the stable context function) rather than the
  // label, so renaming a button cannot silently un-gate it.
  const canCreatePatient   = canOr("patients.create", true);
  const canBookAppointment = canOr("appointments.create", true);
  // Shortcuts that navigate somewhere permission-gated. Fallbacks mirror who
  // could reach each page before RBAC (App.tsx / Sidebar use the same ones).
  const canViewAppointments = canOr("appointments.view", true);
  const canViewPatients     = canOr("patients.view", true);
  const canCreateBill       = canOr("billing.write", true);
  const canManageDoctors    = canOr("doctors.manage", isAdminRole);
  const canViewReports      = canOr("reports.view_clinic", isAdminRole);
  const canWriteVisits      = canOr("visits.write", isAdminRole || isDoctor);
  const canViewVisits       = canOr("visits.view", isAdminRole || isDoctor);
  const canOpenRoster       = canAnyOr(
    ["doctors.view", "doctors.manage_schedule", "doctors.update_status"], isAdminRole || isDoctor,
  );
  // An action whose onClick is an inline navigate() carries `visible` instead,
  // since there is no stable function to match on.
  const gateActions = <T extends { onClick: () => void; visible?: boolean }>(actions: T[]): T[] =>
    actions.filter((a) => {
      if (a.visible === false) return false;
      if (a.onClick === openAddPatientModal) return canCreatePatient;
      if (a.onClick === openBookAppointmentModal) return canBookAppointment;
      return true;
    });

  const {
    flags: featureFlags,
    loading: flagsLoading,
    isAdmin,
    nearestExpiry,
    expiredFlags,
    trialsEndingThisWeek,
    severity,
    subscriptionExpired,
    getGateState,
    getFlag,
  } = useFeatureFlags();

  const reportGate = getGateState("advanced_reports");
  const reportFlag = getFlag("advanced_reports");
  const isGated = !flagsLoading && (reportGate === "locked" || reportGate === "expired");

  const handleUpgradeClick = () => navigate("/settings", { state: { section: "finance" } });
  const handleNotifyAdmin = () => {
    window.location.href =
      "mailto:?subject=Upgrade%20Kaagazy%20EMR%20Subscription&body=Hi%2C%20please%20upgrade%20our%20clinic%20subscription%20to%20continue%20using%20premium%20features.";
  };

  // Header info — fires once, independent of role state.
  useEffect(() => {
    SettingsAPI.getProfile()
      .then((p) => {
        const full = `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim();
        if (full) setUserName(full);
      })
      .catch(() => {});
  }, []);

  // Pending visit count — Doctor only.
  useEffect(() => {
    if (!roleLoaded || !isDoctor) return;
    DashboardAPI.getPendingVisitCount()
      .then((r) => setPendingVisitCount(r.pending_visit_count))
      .catch(() => {});
  }, [roleLoaded, isDoctor]);

  // Doctor summary — own appointments today, draft visits, finalized (active)
  // prescriptions, and clinic-wide patient count. Sourced from /dashboard/summary.
  useEffect(() => {
    if (!roleLoaded || !isDoctor) return;
    DashboardAPI.getRoleSummary()
      .then((s) => setDoctorSummary(s.doctor))
      .catch(() => {});
  }, [roleLoaded, isDoctor]);

  // Reception summary — clinic-wide front-desk KPIs (all doctors). Sourced from
  // /dashboard/summary; the older /dashboard/reception-kpis route does not exist.
  useEffect(() => {
    if (!roleLoaded || !isReception) return;
    DashboardAPI.getRoleSummary()
      .then((s) => setReceptionSummary(s.reception))
      .catch(() => {});
  }, [roleLoaded, isReception]);

  // Clinic-wide metrics — Admin only.
  useEffect(() => {
    if (!roleLoaded || !isAdminRole) {
      if (roleLoaded) setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    DashboardAPI.getMetrics()
      .then((data) => { if (!cancelled) { setMetrics(data); setError(null); } })
      .catch((e) => {
        if (cancelled) return;
        logger.error("dashboard.metrics failed", e instanceof Error ? e : undefined);
        setError(e instanceof Error ? e.message : "Failed to load dashboard");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [roleLoaded, isAdminRole, metricsKey]);

  // Sync reportPreset → reportFrom/reportTo
  useEffect(() => {
    if (reportPreset === "custom") return;
    setReportFrom(_isoDaysAgo(Number(reportPreset)));
    setReportTo(_todayIso());
  }, [reportPreset]);

  // Fetch report data when a report tab is active
  useEffect(() => {
    const REPORT_TABS: DashboardTab[] = ["Revenue", "Patient", "Diagnoses"];
    if (!REPORT_TABS.includes(activeTab)) return;
    if (flagsLoading || isGated) return;

    let cancelled = false;
    setReportLoading(true);
    setReportError(null);

    const tasks: Promise<void>[] = [];
    if (activeTab === "Patient") {
      tasks.push(ReportsAPI.patients(reportFrom, reportTo).then((d) => { if (!cancelled) setReportPatients(d); }));
    } else if (activeTab === "Revenue") {
      tasks.push(ReportsAPI.revenue(reportFrom, reportTo).then((d) => { if (!cancelled) setReportRevenue(d); }));
    } else if (activeTab === "Diagnoses") {
      tasks.push(ReportsAPI.diagnoses(reportFrom, reportTo).then((d) => { if (!cancelled) setReportDiagnoses(d); }));
    }

    Promise.all(tasks)
      .catch((e: Error) => { if (!cancelled) setReportError(e.message || "Failed to load report"); })
      .finally(() => { if (!cancelled) setReportLoading(false); });

    return () => { cancelled = true; };
  }, [activeTab, reportFrom, reportTo, flagsLoading, isGated]);

  const handleReportExport = async (type: "patients" | "revenue" | "diagnoses") => {
    setReportExporting(true);
    try { await ReportsAPI.downloadCsv(type, reportFrom, reportTo); }
    catch (e) { logger.error("report export failed", e instanceof Error ? e : undefined); }
    finally { setReportExporting(false); }
  };

  useEffect(() => {
    ClinicGroupAPI.getMine().then(setMyGroup).catch(() => setMyGroup(null));
  }, []);

  // Accounting data — loads whenever the Accounting tab is active, independent
  // of isGated (Accounting is Admin-only, never plan-gated).
  useEffect(() => {
    if (activeTab !== "Accounting") return;

    let cancelled = false;
    setAccountingLoading(true);
    setAccountingError(null);

    const tasks = [
      ReportsAPI.pnl(reportFrom, reportTo, pnlPeriod).then((d) => { if (!cancelled) setPnl(d); }),
      ReportsAPI.cashFlow(reportFrom, reportTo).then((d) => { if (!cancelled) setCashFlow(d); }),
      ReportsAPI.breakdown(reportFrom, reportTo).then((d) => { if (!cancelled) setBreakdown(d); }),
    ];
    if (myGroup) {
      tasks.push(
        ReportsAPI.consolidatedPnl(myGroup.clinic_group_id, reportFrom, reportTo, pnlPeriod)
          .then((d) => { if (!cancelled) setConsolidatedRows(d); })
      );
    }

    Promise.all(tasks)
      .catch((e: Error) => {
        if (!cancelled) {
          logger.error("accounting reports load failed", e);
          setAccountingError(e.message || "Failed to load accounting reports");
        }
      })
      .finally(() => { if (!cancelled) setAccountingLoading(false); });

    return () => { cancelled = true; };
  }, [activeTab, reportFrom, reportTo, pnlPeriod, myGroup]);

  const consolidatedPnlBuckets = React.useMemo((): PnlBucket[] => {
    if (!consolidatedRows) return [];
    const filtered = selectedBranch === "all"
      ? consolidatedRows
      : consolidatedRows.filter((r) => r.clinic_id === selectedBranch);

    const byBucket = new Map<string, { income: number; expense: number }>();
    for (const row of filtered) {
      const entry = byBucket.get(row.bucket) ?? { income: 0, expense: 0 };
      entry.income += row.income_paise;
      entry.expense += row.expense_paise;
      byBucket.set(row.bucket, entry);
    }
    return Array.from(byBucket.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([bucket, v]) => ({
        bucket, income_paise: v.income, expense_paise: v.expense,
        net_paise: v.income - v.expense, is_expense_anomaly: false,
      }));
  }, [consolidatedRows, selectedBranch]);

  const handleAccountingExport = async (format: "csv" | "pdf") => {
    setAccountingExporting(true);
    try {
      if (accountingSubTab === "pnl") {
        await (format === "csv" ? ReportsAPI.downloadPnlCsv(reportFrom, reportTo, pnlPeriod) : ReportsAPI.downloadPnlPdf(reportFrom, reportTo, pnlPeriod));
      } else if (accountingSubTab === "cash-flow") {
        await (format === "csv" ? ReportsAPI.downloadCashFlowCsv(reportFrom, reportTo) : ReportsAPI.downloadCashFlowPdf(reportFrom, reportTo));
      } else {
        await (format === "csv" ? ReportsAPI.downloadBreakdownCsv(reportFrom, reportTo) : ReportsAPI.downloadBreakdownPdf(reportFrom, reportTo));
      }
    } catch (e) {
      logger.error("accounting export failed", e instanceof Error ? e : undefined);
    } finally {
      setAccountingExporting(false);
    }
  };

  // Shared date string
  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <div className="min-h-screen bg-[#f5f5f7] flex">
      <Sidebar />

      <div className="flex-1 min-w-0 pl-0 lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0">
        <AppHeader title="Dashboard">
          <DownloadReportButton userRole={null} />
        </AppHeader>

        {/* S1 — Global Trial Banner */}
        {!flagsLoading && severity && (
          <TrialBanner
            nearestExpiry={nearestExpiry}
            expiredFlags={expiredFlags}
            trialsEndingThisWeek={trialsEndingThisWeek}
            severity={severity}
            isAdmin={isAdmin}
            onUpgradeClick={handleUpgradeClick}
            onViewDetails={() => setShowTrialDetail(true)}
            onNotifyAdmin={handleNotifyAdmin}
          />
        )}

        <div className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 space-y-5">
          {/* Dev-only visual QA: the banner's copy is driven by a hard-coded
              MOCK_CLOSURE_DATE, not a real clinic field (Phase 1 not merged
              yet) — never show it to a real clinic. Same gating convention
              as DevRoleSwitcher / DevQuickLinks. */}
          {process.env.NODE_ENV === "development" && <ClinicClosureBanner />}

          {/* ── Loading skeleton while role resolves ── */}
          {!roleLoaded && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-32 rounded-2xl bg-white border border-slate-100 shadow-sm animate-pulse" />
              ))}
            </div>
          )}

          {/* ── Doctor view ── */}
          {roleLoaded && isDoctor && (
            <div className="space-y-5">
              {/* Header row */}
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-semibold text-slate-900">Dr. {humanLabel(userName) || "Doctor"}</h1>
                  <p className="text-xs text-slate-500 mt-0.5">Doctor</p>
                </div>
                <span className="text-xs font-medium text-slate-500">{today}</span>
              </div>

              {/* KPI row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCardComponent
                  accent="fuchsia"
                  label="My Appointments Today"
                  value={
                    doctorSummary?.appointments_today != null
                      ? doctorSummary.appointments_today.toLocaleString("en-IN")
                      : "—"
                  }
                  changePct={null}
                  direction={undefined}
                  onClick={canViewAppointments ? () => navigate("/appointments") : undefined}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <path d="M16 2v4M8 2v4M3 10h18" />
                    </svg>
                  }
                />
                <KpiCardComponent
                  accent="orange"
                  label="My Pending Visits"
                  value={
                    doctorSummary?.draft_visits != null
                      ? doctorSummary.draft_visits.toLocaleString("en-IN")
                      : pendingVisitCount !== null
                        ? pendingVisitCount.toLocaleString("en-IN")
                        : "—"
                  }
                  changePct={null}
                  direction={undefined}
                  onClick={canWriteVisits ? () => navigate("/pending-visits") : undefined}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                    </svg>
                  }
                />
                <KpiCardComponent
                  accent="green"
                  label="My Prescriptions"
                  value={
                    doctorSummary?.prescriptions_total != null
                      ? doctorSummary.prescriptions_total.toLocaleString("en-IN")
                      : "—"
                  }
                  changePct={null}
                  direction={undefined}
                  onClick={canViewVisits ? () => navigate("/my-prescriptions") : undefined}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" />
                      <path d="m8.5 8.5 7 7" />
                    </svg>
                  }
                />
                <KpiCardComponent
                  accent="blue"
                  label="Patients"
                  value={
                    doctorSummary?.clinic_total_patients != null
                      ? doctorSummary.clinic_total_patients.toLocaleString("en-IN")
                      : "—"
                  }
                  changePct={null}
                  direction={undefined}
                  onClick={canViewPatients ? () => navigate("/patients") : undefined}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  }
                />
              </div>

              {/* Action cards: Set Availability · Contact Support · Daily Tip */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Set Availability */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center text-orange-500 shrink-0">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </div>
                    <p className="text-sm font-semibold text-slate-900">Set Availability</p>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">Manage your weekly schedule, time-off, and appointment slots.</p>
                  {canOpenRoster && (
                    <button
                      onClick={() => navigate("/roster")}
                      className="mt-auto text-xs font-semibold text-orange-600 hover:text-orange-700 flex items-center gap-1"
                    >
                      Open Roster
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Contact Support */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center text-blue-500 shrink-0">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                    </div>
                    <p className="text-sm font-semibold text-slate-900">Contact Support</p>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">Reach our team for help — we typically respond within a few hours.</p>
                  <a
                    href="mailto:support@swasthiq.in?subject=Doctor%20Support%20Request"
                    className="mt-auto text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    Email Support
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>

                {/* Daily Tips */}
                <DailyTipCard />
              </div>

              {/* Today's appointments */}
              <TodaysAppointments items={[]} />
            </div>
          )}

          {/* ── Receptionist view ── */}
          {roleLoaded && isReception && (
            <div className="space-y-5">
              {/* Header row */}
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-semibold text-slate-900">Front desk</h1>
                  <p className="text-xs text-slate-500 mt-0.5">Receptionist</p>
                </div>
                <span className="text-xs font-medium text-slate-500">{today}</span>
              </div>

              {/* KPI row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCardComponent
                  accent="fuchsia"
                  label="Today's Appointments"
                  value={
                    receptionSummary?.appointments_today != null
                      ? receptionSummary.appointments_today.toLocaleString("en-IN")
                      : "—"
                  }
                  changePct={null}
                  direction={undefined}
                  onClick={canViewAppointments ? () => navigate("/appointments") : undefined}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <path d="M16 2v4M8 2v4M3 10h18" />
                    </svg>
                  }
                />
                <KpiCardComponent
                  accent="orange"
                  label="Walk-ins Waiting"
                  value={
                    receptionSummary?.walk_ins_waiting != null
                      ? receptionSummary.walk_ins_waiting.toLocaleString("en-IN")
                      : "—"
                  }
                  changePct={null}
                  direction={undefined}
                  onClick={canViewAppointments ? () => navigate("/appointments/queue") : undefined}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <circle cx="12" cy="5" r="2" />
                      <path d="m19 10-7 1-4 8" />
                      <path d="m12 11-2 5" />
                      <path d="m5 10 3 1" />
                    </svg>
                  }
                />
                <KpiCardComponent
                  accent="green"
                  label="Pending Payments"
                  value={
                    receptionSummary?.pending_payments_paise != null
                      ? formatINR(receptionSummary.pending_payments_paise)
                      : "—"
                  }
                  changePct={null}
                  direction={undefined}
                  onClick={canCreateBill ? () => navigate("/billing") : undefined}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                    </svg>
                  }
                />
                <KpiCardComponent
                  accent="blue"
                  label="Registered Today"
                  value={
                    receptionSummary?.registered_today != null
                      ? receptionSummary.registered_today.toLocaleString("en-IN")
                      : "—"
                  }
                  changePct={null}
                  direction={undefined}
                  onClick={canViewPatients ? () => navigate("/patients") : undefined}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M19 8v6M22 11h-6" />
                    </svg>
                  }
                />
              </div>

              {/* Quick actions */}
              <QuickActions
                actions={gateActions([
                  {
                    label: "Register Patient",
                    accent: "blue",
                    onClick: openAddPatientModal,
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M19 8v6M22 11h-6" />
                      </svg>
                    ),
                  },
                  {
                    label: "Book Appt",
                    accent: "fuchsia",
                    onClick: openBookAppointmentModal,
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <path d="M16 2v4M8 2v4M3 10h18" />
                      </svg>
                    ),
                  },
                  {
                    label: "Check-in",
                    accent: "orange",
                    onClick: () => navigate("/appointments"),
                    visible: canViewAppointments,
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>
                    ),
                  },
                  {
                    label: "Create Bill",
                    accent: "green",
                    onClick: () => navigate("/billing"),
                    visible: canCreateBill,
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <line x1="10" y1="9" x2="8" y2="9" />
                      </svg>
                    ),
                  },
                ])}
              />

              {/* Today's appointments */}
              <TodaysAppointments items={[]} />
            </div>
          )}

          {/* ── Admin view (and fallback for unknown roles) ── */}
          {roleLoaded && (isAdminRole || (!isDoctor && !isReception)) && (
            <>
              {/* Onboarding / KYC banner (non-credentialed admin) */}
              {!can_prescribe && (
                <div className="bg-[#fffbeb] border border-[#ea580c] rounded-xl flex items-center justify-between px-4 py-3.5">
                  <div className="flex gap-3 items-start">
                    <svg className="w-5 h-5 text-[#ea580c] shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-sm font-semibold text-[#ea580c]">Pending verification - reviewed same business day</p>
                      <p className="text-xs text-slate-800 mt-0.5 max-w-xl">You can explore Kaagazy, but creating or editing patient records is blocked until your clinic is credentialed.</p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate("/settings?section=compliance&card=clinic-credentialing")}
                    className="shrink-0 ml-4 bg-[#ea580c] text-white text-sm font-semibold px-3.5 min-h-[44px] rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    View KYC status
                  </button>
                </div>
              )}

              <DashboardTabs active={activeTab} onChange={setActiveTab} />

              {/* ── Overview tab ── */}
              {activeTab === "Overview" && error && (
                <div className="rounded-2xl bg-red-50 border border-red-100 px-5 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              {activeTab === "Overview" && loading && !metrics && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="h-32 rounded-2xl bg-white border border-slate-100 shadow-sm animate-pulse" />
                  ))}
                </div>
              )}

              {activeTab === "Overview" && metrics && (
                <>
                  {/* KPI row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <KpiCardComponent
                      accent="blue"
                      label="Total Patients"
                      value={metrics.total_patients.value.toLocaleString("en-IN")}
                      changePct={metrics.total_patients.change_pct}
                      direction={metrics.total_patients.direction}
                      onClick={canViewPatients ? () => navigate("/patients") : undefined}
                      icon={
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                      }
                    />
                    <KpiCardComponent
                      accent="fuchsia"
                      label="Appointments Today"
                      value={metrics.appointments_today.value.toLocaleString("en-IN")}
                      changePct={metrics.appointments_today.change_pct}
                      direction={metrics.appointments_today.direction}
                      onClick={canViewAppointments ? () => navigate("/appointments") : undefined}
                      icon={
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                          <rect x="3" y="4" width="18" height="18" rx="2" />
                          <path d="M16 2v4M8 2v4M3 10h18" />
                        </svg>
                      }
                    />
                    <KpiCardComponent
                      accent="green"
                      label="Revenue (MTD)"
                      value={
                        metrics.revenue_mtd_paise
                          ? formatINR(metrics.revenue_mtd_paise.value)
                          : "—"
                      }
                      changePct={metrics.revenue_mtd_paise?.change_pct ?? null}
                      direction={metrics.revenue_mtd_paise?.direction}
                      blocked={!metrics.revenue_mtd_paise}
                      onClick={metrics.revenue_mtd_paise && canViewReports ? () => navigate("/reports") : undefined}
                      icon={
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                        </svg>
                      }
                    />
                    <KpiCardComponent
                      accent="orange"
                      label="Active Doctors"
                      value={`${metrics.active_doctors.value}/${metrics.active_doctors_total}`}
                      changePct={metrics.active_doctors.change_pct}
                      direction={metrics.active_doctors.direction}
                      onClick={() => navigate("/doctors")}
                      icon={
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                          <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6 6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
                          <path d="M8 15v1a6 6 0 0 0 6 6 6 6 0 0 0 6-6v-4" />
                          <circle cx="20" cy="10" r="2" />
                        </svg>
                      }
                    />
                  </div>

                  {/* Main two-column grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Left column (spans 2) */}
                    <div className="lg:col-span-2 space-y-4">
                      <QuickActions
                        actions={gateActions([
                          {
                            label: "New Patient",
                            accent: "blue",
                            onClick: openAddPatientModal,
                            icon: (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M19 8v6M22 11h-6" />
                              </svg>
                            ),
                          },
                          {
                            label: "Book Appointment",
                            accent: "fuchsia",
                            onClick: openBookAppointmentModal,
                            icon: (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                <rect x="3" y="4" width="18" height="18" rx="2" />
                                <path d="M16 2v4M8 2v4M3 10h18" />
                              </svg>
                            ),
                          },
                          {
                            label: "Add Doctor",
                            accent: "orange",
                            onClick: () => navigate("/doctors"),
                            visible: canManageDoctors,
                            icon: (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6 6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
                                <path d="M8 15v1a6 6 0 0 0 6 6 6 6 0 0 0 6-6v-4" />
                                <circle cx="20" cy="10" r="2" />
                              </svg>
                            ),
                          },
                          {
                            label: "Export",
                            accent: "slate",
                            onClick: () => navigate("/reports"),
                            visible: canViewReports,
                            icon: (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                              </svg>
                            ),
                          },
                        ])}
                      />

                      {/* Half-width each: Today's Appointments + Patient Activity, then
                          Revenue Trend + Prescriptions — no gap between these two rows.
                          Cards stretch to match height (no items-start) so a short
                          card (e.g. "No appointments today") doesn't leave a visible
                          gap of its own next to a taller sibling in the same row. */}
                      <div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <TodaysAppointments items={metrics.todays_appointments} onActionComplete={refreshMetrics} />
                          <PatientActivityCard activity={metrics.patient_activity} />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <RevenueTrendCard
                            amountPaise={metrics.revenue_mtd_paise?.value ?? null}
                            changePct={metrics.revenue_mtd_paise?.change_pct ?? null}
                            series={metrics.revenue_trend.map((r) => {
                              const d = new Date(`${r.bucket}T00:00:00`);
                              const day = d.toLocaleDateString("en-IN", { weekday: "short" });
                              return { day, amount: r.amount_paise };
                            })}
                          />
                          <PrescriptionsCard stats={metrics.prescription_stats} blocked={isGated} />
                        </div>
                      </div>
                    </div>

                    {/* Right column */}
                    <div className="space-y-4">
                      <ActiveDoctorsPanel
                        active={metrics.active_doctors.value}
                        total={metrics.active_doctors_total}
                        doctors={metrics.active_doctors_list}
                        onViewAll={() => navigate("/doctors")}
                      />
                      <DepartmentOverview
                        rows={metrics.department_overview}
                        onViewAll={() => navigate("/doctors")}
                      />
                      <SystemAlerts alerts={metrics.system_alerts} />
                    </div>
                  </div>
                </>
              )}

              {/* ── Prescriptions tab ── */}
              {activeTab === "Prescriptions" && (
                <>
                  {flagsLoading && <div className="h-72 rounded-2xl bg-white border border-slate-100 shadow-sm animate-pulse" />}
                  {!flagsLoading && isGated && (
                    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm">
                      <LockedFeatureEmptyState
                        featureLabel="Advanced Reports & Analytics"
                        description="Upgrade your plan to access patient trends, revenue, appointment utilisation and diagnosis analytics."
                        isAdmin={isAdmin}
                        variant={reportGate === "expired" ? "expired" : "locked"}
                        expiredDate={reportFlag?.expires_at ?? null}
                        onUpgradeClick={() => setShowPlansModal(true)}
                        onNotifyAdmin={handleNotifyAdmin}
                      />
                    </div>
                  )}
                  {!flagsLoading && !isGated && (
                    metrics
                      ? <PrescriptionsCard stats={metrics.prescription_stats} />
                      : <div className="h-32 rounded-2xl bg-white border border-slate-100 shadow-sm animate-pulse" />
                  )}
                </>
              )}

              {/* ── Revenue / Patient / Diagnoses report tabs ── */}
              {(activeTab === "Revenue" || activeTab === "Patient" || activeTab === "Diagnoses") && (
                <>
                  {/* Feature gate */}
                  {flagsLoading && <div className="h-72 rounded-2xl bg-white border border-slate-100 shadow-sm animate-pulse" />}
                  {!flagsLoading && isGated && (
                    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm">
                      <LockedFeatureEmptyState
                        featureLabel="Advanced Reports & Analytics"
                        description="Upgrade your plan to access patient trends, revenue, appointment utilisation and diagnosis analytics."
                        isAdmin={isAdmin}
                        variant={reportGate === "expired" ? "expired" : "locked"}
                        expiredDate={reportFlag?.expires_at ?? null}
                        onUpgradeClick={() => setShowPlansModal(true)}
                        onNotifyAdmin={handleNotifyAdmin}
                      />
                    </div>
                  )}

                  {!flagsLoading && !isGated && (
                    <>
                      {/* Date range + export */}
                      <div className="rounded-2xl bg-white border border-slate-100 shadow-sm px-5 py-4 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-medium text-slate-500 mr-1">Range:</span>
                          {(["7", "30", "90", "custom"] as const).map((v) => (
                            <button
                              key={v}
                              onClick={() => setReportPreset(v)}
                              className={`text-xs font-semibold px-3 min-h-[44px] rounded-lg border transition-colors ${
                                reportPreset === v
                                  ? "bg-blue-600 text-white border-blue-600"
                                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                              }`}
                            >
                              {v === "custom" ? "Custom" : `Last ${v} days`}
                            </button>
                          ))}
                          {reportPreset === "custom" && (
                            <div className="flex items-center gap-2 ml-2">
                              <DateInput value={reportFrom} max={reportTo} onChange={(e) => setReportFrom(e.target.value)} className="text-xs px-2 py-1.5 border border-slate-200 rounded-lg" />
                              <span className="text-xs text-slate-400">to</span>
                              <DateInput value={reportTo} min={reportFrom} max={_todayIso()} onChange={(e) => setReportTo(e.target.value)} className="text-xs px-2 py-1.5 border border-slate-200 rounded-lg" />
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleReportExport(activeTab === "Revenue" ? "revenue" : activeTab === "Patient" ? "patients" : "diagnoses")}
                          disabled={reportExporting}
                          className="text-xs font-semibold px-4 min-h-[44px] rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                          </svg>
                          {reportExporting ? "Exporting…" : `Export CSV`}
                        </button>
                      </div>

                      {reportError && (
                        <div className="rounded-2xl bg-red-50 border border-red-100 px-5 py-3 text-sm text-red-700">{reportError}</div>
                      )}
                      {reportLoading && <div className="h-72 rounded-2xl bg-white border border-slate-100 shadow-sm animate-pulse" />}
                      {!reportLoading && activeTab === "Patient" && (
                        reportPatientsCount < REPORT_MIN_DATA_POINTS
                          ? <SectionCard title="Patient Registration Trend">
                              <NotEnoughDataEmptyState metricLabel="patients" threshold={REPORT_MIN_DATA_POINTS} current={reportPatientsCount} />
                            </SectionCard>
                          : <PatientsReport data={reportPatients ?? []} />
                      )}
                      {!reportLoading && activeTab === "Revenue" && (
                        reportRevenueCount < REPORT_MIN_DATA_POINTS
                          ? <SectionCard title="Revenue Trend">
                              <NotEnoughDataEmptyState metricLabel="bills" threshold={REPORT_MIN_DATA_POINTS} current={reportRevenueCount} />
                            </SectionCard>
                          : <RevenueReport data={reportRevenue ?? []} />
                      )}
                      {!reportLoading && activeTab === "Diagnoses" && (
                        reportDiagnosesCount < REPORT_MIN_DATA_POINTS
                          ? <SectionCard title="Top Diagnoses">
                              <NotEnoughDataEmptyState metricLabel="diagnosis visits" threshold={REPORT_MIN_DATA_POINTS} current={reportDiagnosesCount} />
                            </SectionCard>
                          : <DiagnosesReport rows={reportDiagnoses ?? []} />
                      )}
                    </>
                  )}
                </>
              )}

              {/* ── Accounting tab — Admin-only, NOT feature-flag gated ── */}
              {activeTab === "Accounting" && (
                <>
                  <ClinicGroupPanel group={myGroup} onChange={setMyGroup} />

                  {myGroup && accountingSubTab === "pnl" && (
                    <div className="rounded-xl bg-white border border-slate-100 shadow-sm px-4 py-3 flex flex-wrap items-center gap-3">
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={consolidated}
                          onChange={(e) => setConsolidated(e.target.checked)}
                          className="rounded border-slate-300"
                        />
                        Consolidated view ({myGroup.name})
                      </label>
                      {consolidated && (
                        <select
                          value={selectedBranch}
                          onChange={(e) => setSelectedBranch(e.target.value)}
                          className="text-xs px-2 py-1.5 border border-slate-200 rounded-lg"
                        >
                          <option value="all">All branches</option>
                          {myGroup.member_clinics.map((c) => (
                            <option key={c.clinic_id} value={c.clinic_id}>{c.name}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  <div className="rounded-2xl bg-white border border-slate-100 shadow-sm px-5 py-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-slate-500 mr-1">Range:</span>
                      {(["7", "30", "90", "custom"] as const).map((v) => (
                        <button
                          key={v}
                          onClick={() => setReportPreset(v)}
                          className={`text-xs font-semibold px-3 min-h-[44px] rounded-lg border transition-colors ${
                            reportPreset === v
                              ? "bg-blue-600 text-white border-blue-600"
                              : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          {v === "custom" ? "Custom" : `Last ${v} days`}
                        </button>
                      ))}
                      {reportPreset === "custom" && (
                        <div className="flex items-center gap-2 ml-2">
                          <DateInput value={reportFrom} max={reportTo} onChange={(e) => setReportFrom(e.target.value)} className="text-xs px-2 py-1.5 border border-slate-200 rounded-lg" />
                          <span className="text-xs text-slate-400">to</span>
                          <DateInput value={reportTo} min={reportFrom} max={_todayIso()} onChange={(e) => setReportTo(e.target.value)} className="text-xs px-2 py-1.5 border border-slate-200 rounded-lg" />
                        </div>
                      )}
                      {accountingSubTab === "pnl" && (
                        <select
                          value={pnlPeriod}
                          onChange={(e) => setPnlPeriod(e.target.value as PnlPeriod)}
                          className="text-xs px-2 py-1.5 border border-slate-200 rounded-lg ml-2"
                        >
                          <option value="month">Monthly</option>
                          <option value="quarter">Quarterly</option>
                          <option value="year">Yearly</option>
                        </select>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleAccountingExport("csv")}
                        disabled={accountingExporting}
                        className="text-xs font-semibold px-3 min-h-[44px] rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:bg-slate-300"
                      >
                        {accountingExporting ? "…" : "CSV"}
                      </button>
                      <button
                        onClick={() => handleAccountingExport("pdf")}
                        disabled={accountingExporting}
                        className="text-xs font-semibold px-3 min-h-[44px] rounded-lg bg-slate-700 text-white hover:bg-slate-600 disabled:bg-slate-300"
                      >
                        {accountingExporting ? "…" : "PDF"}
                      </button>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-2 py-2 flex items-center gap-1 overflow-x-auto">
                    {([
                      { key: "pnl" as const,        label: "P&L" },
                      { key: "cash-flow" as const,  label: "Cash Flow" },
                      { key: "breakdown" as const,  label: "Breakdown" },
                    ]).map((t) => {
                      const isActive = t.key === accountingSubTab;
                      return (
                        <button
                          key={t.key}
                          onClick={() => setAccountingSubTab(t.key)}
                          className={`text-sm font-medium px-4 py-2 rounded-xl transition-colors whitespace-nowrap ${
                            isActive ? "bg-blue-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>

                  {accountingError && (
                    <div className="rounded-2xl bg-red-50 border border-red-100 px-5 py-3 text-sm text-red-700">{accountingError}</div>
                  )}

                  {accountingLoading && <div className="h-72 rounded-2xl bg-white border border-slate-100 shadow-sm animate-pulse" />}

                  {!accountingLoading && accountingSubTab === "pnl" && (
                    <PnlReport data={consolidated ? consolidatedPnlBuckets : (pnl ?? [])} />
                  )}
                  {!accountingLoading && accountingSubTab === "cash-flow" && <CashFlowReport data={cashFlow ?? []} />}
                  {!accountingLoading && accountingSubTab === "breakdown" && <BreakdownReport rows={breakdown ?? []} />}

                  <p className="text-[11px] text-slate-400 px-1">
                    Anomaly highlights are a simple trailing-average heuristic, not fraud detection — always double-check flagged buckets before acting on them.
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </div>
      {showPlansModal && <PlansModal onClose={() => setShowPlansModal(false)} />}
      {/* S2 — Subscription/Trial Expired Post-Login Modal */}
      {!flagsLoading && (expiredFlags.length > 0 || subscriptionExpired) && (
        <TrialExpiredModal
          expiredFlags={expiredFlags}
          isAdmin={isAdmin}
          onUpgradeClick={handleUpgradeClick}
          onNotifyAdmin={handleNotifyAdmin}
          subscriptionExpired={subscriptionExpired}
        />
      )}

      {/* S3 — Trial Status Detail Modal */}
      {showTrialDetail && (
        <TrialStatusDetail
          flags={featureFlags}
          isAdmin={isAdmin}
          onClose={() => setShowTrialDetail(false)}
          onUpgradeClick={handleUpgradeClick}
          onNotifyAdmin={handleNotifyAdmin}
        />
      )}
    </div>
  );
};

export default DashboardPage;
